import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, Users, AlertTriangle, Mail } from 'lucide-react';

// Offer additional work to whoever is free.
//
// The pills come from work_offer_kinds, so adding "Depot swap" is a row, not a
// deploy — the list here is never hardcoded.
//
// The audience is shown BEFORE sending, with how many of them can actually be
// reached on a phone. "Offered to 6" and "6 will see it now" are different
// facts and the second is the one that decides whether the job gets covered.

const todayISO = () => new Date().toISOString().slice(0, 10);

const WorkOfferComposer = ({ open, onOpenChange, onSent }) => {
  const { toast } = useToast();
  const [kinds, setKinds] = useState([]);
  const [depots, setDepots] = useState([]);
  const [kindKey, setKindKey] = useState(null);
  const [offerDate, setOfferDate] = useState(todayISO());
  const [startTime, setStartTime] = useState('');
  const [headcount, setHeadcount] = useState(1);
  const [notes, setNotes] = useState('');
  const [depotId, setDepotId] = useState('');
  const [autoSend, setAutoSend] = useState(false);
  const [includeAllocated, setIncludeAllocated] = useState(false);
  // 'free' = everyone free that day (the default), 'crew' = one crew,
  // 'people' = named individuals.
  const [audienceMode, setAudienceMode] = useState('free');
  const [crewId, setCrewId] = useState('');
  const [pickedIds, setPickedIds] = useState([]);
  const [crews, setCrews] = useState([]);
  const [allCrew, setAllCrew] = useState([]);
  const [personSearch, setPersonSearch] = useState('');
  const [audience, setAudience] = useState(null);
  const [sending, setSending] = useState(false);
  // What accepting DOES. 'none' is the default and is exactly how every offer
  // has always behaved, so an admin who ignores this section changes nothing.
  //
  // allocCrewId is the crew someone would be put ON. It is not crewId above,
  // which narrows who is ASKED — asking one crew and allocating to another is
  // a normal thing to want, so the two are kept apart deliberately.
  const [outcomeKind, setOutcomeKind] = useState('none');
  const [allocCrewId, setAllocCrewId] = useState('');
  const [allocRole, setAllocRole] = useState('porter');
  const [allocDepotId, setAllocDepotId] = useState('');
  const [allocIsOtr, setAllocIsOtr] = useState(false);
  const [allocOtrDepotId, setAllocOtrDepotId] = useState('');
  const [allocIsRetraining, setAllocIsRetraining] = useState(false);
  const [closesAt, setClosesAt] = useState('');

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [{ data: k }, { data: d }, { data: cr }, { data: people }] = await Promise.all([
        supabase.from('work_offer_kinds').select('*').eq('active', true).order('sort_order'),
        supabase.from('depots').select('id, name').order('name'),
        supabase.rpc('work_offer_crews'),
        supabase.rpc('work_offer_selectable_crew'),
      ]);
      setKinds(k || []);
      setDepots(d || []);
      setCrews(cr || []);
      setAllCrew(people || []);
      if (!kindKey && (k || []).length) {
        setKindKey(k[0].key);
        setHeadcount(k[0].default_headcount || 1);
      }
    })();
  }, [open, kindKey]);

  // Who would get this, recomputed whenever the answer would change.
  const loadAudience = useCallback(async () => {
    if (!offerDate) return;
    if (audienceMode === 'people' && pickedIds.length === 0) { setAudience([]); return; }
    if (audienceMode === 'crew' && !crewId) { setAudience([]); return; }
    const { data, error } = await supabase.rpc('work_offer_available_crew', {
      p_date: offerDate,
      p_include_allocated: includeAllocated,
      p_subcontractor_ids: audienceMode === 'people' ? pickedIds : null,
      p_crew_id: audienceMode === 'crew' ? crewId : null,
    });
    if (error) { setAudience(null); return; }
    setAudience(data || []);
  }, [offerDate, includeAllocated, audienceMode, crewId, pickedIds]);
  useEffect(() => { if (open) loadAudience(); }, [open, loadAudience]);

  const kind = kinds.find((k) => k.key === kindKey);
  const reachable = (audience || []).filter((a) => a.reachable).length;

  // The table's CHECK constraints are what actually guarantee this; saying it
  // here as well means the admin finds out while composing rather than after
  // pressing Send. The message on the way back is the constraint's, mapped.
  const allocIncomplete = outcomeKind === 'allocation'
    && (!allocCrewId || !allocRole || !allocDepotId || (allocIsOtr && !allocOtrDepotId));

  const send = async () => {
    if (!kindKey) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('dispatch-work-offer', {
        body: {
          action: 'send',
          kind_key: kindKey,
          offer_date: offerDate,
          start_time: startTime || null,
          headcount: Number(headcount) || 1,
          notes: notes || null,
          depot_id: depotId || null,
          auto_send_email: autoSend,
          include_allocated: includeAllocated,
          subcontractor_ids: audienceMode === 'people' ? pickedIds : null,
          crew_id: audienceMode === 'crew' ? (crewId || null) : null,
          closes_at: closesAt || null,
          outcome_kind: outcomeKind === 'none' ? null : outcomeKind,
          alloc: outcomeKind === 'allocation' ? {
            crew_id: allocCrewId || null,
            role: allocRole || null,
            depot_id: allocDepotId || null,
            is_otr: allocIsOtr,
            otr_depot_id: allocIsOtr ? (allocOtrDepotId || null) : null,
            is_retraining: allocIsRetraining,
          } : null,
        },
      });
      if (error) throw error;
      if (data?.success === false) {
        // Not an error — a real answer that nobody was free.
        toast({ title: 'Nobody to offer it to', description: data.message, variant: 'destructive' });
        return;
      }
      toast({
        title: 'Offer sent',
        description: `Offered to ${data.offered_to}. ${data.pushed} pushed`
          + (data.without_device ? `, ${data.without_device} with no device (they will see it in Messages).` : '.'),
      });
      onSent?.(data);
      onOpenChange(false);
      setNotes('');
    } catch (e) {
      // supabase-js turns a non-2xx into "Edge Function returned a non-2xx
      // status code" and hides the body on e.context. The body is where the
      // actual reason lives ("An allocation needs a crew, a role and a depot"),
      // so read it — a generic message here is the difference between a fix and
      // another round of guessing.
      let detail = e?.message || 'Something went wrong.';
      try {
        const body = await e?.context?.json?.();
        if (body?.error) detail = body.error;
      } catch { /* no JSON body — keep the generic message */ }
      toast({ title: 'Could not send the offer', description: detail, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Additional work</DialogTitle>
          <DialogDescription>
            Offer a job to the crew who are free. First to accept gets it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Type of work</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {kinds.map((k) => (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => { setKindKey(k.key); setHeadcount(k.default_headcount || 1); }}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    kindKey === k.key
                      ? 'border-indigo-500 bg-indigo-600 text-white'
                      : 'border-border bg-background hover:bg-muted'
                  }`}
                >
                  {k.label}
                </button>
              ))}
              {kinds.length === 0 && (
                <p className="text-xs text-muted-foreground">No work types are switched on.</p>
              )}
            </div>
            {kind?.description && (
              <p className="mt-1.5 text-xs text-muted-foreground">{kind.description}</p>
            )}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Who gets it</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                { k: 'free', label: 'Anyone free' },
                { k: 'crew', label: 'One crew' },
                { k: 'people', label: 'Specific people' },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setAudienceMode(m.k)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    audienceMode === m.k
                      ? 'border-indigo-500 bg-indigo-600 text-white'
                      : 'border-border bg-background hover:bg-muted'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {audienceMode === 'crew' && (
              <select
                value={crewId}
                onChange={(e) => setCrewId(e.target.value)}
                className="mt-2 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Pick a crew…</option>
                {crews.map((c) => (
                  <option key={c.crew_id} value={c.crew_id}>
                    {c.crew_identifier} ({c.members})
                  </option>
                ))}
              </select>
            )}

            {audienceMode === 'people' && (
              <div className="mt-2 space-y-2">
                <Input
                  value={personSearch}
                  onChange={(e) => setPersonSearch(e.target.value)}
                  placeholder="Search by name…"
                  className="h-9"
                />
                <div className="max-h-44 overflow-y-auto rounded-lg border border-border">
                  {allCrew
                    .filter((c) => !personSearch
                      || (c.full_name || '').toLowerCase().includes(personSearch.toLowerCase())
                      || (c.crew_identifier || '').toLowerCase().includes(personSearch.toLowerCase()))
                    .map((c) => {
                      const on = pickedIds.includes(c.subcontractor_id);
                      return (
                        <label key={c.subcontractor_id}
                               className="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-2 text-xs last:border-0 hover:bg-muted">
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => setPickedIds((prev) => on
                              ? prev.filter((x) => x !== c.subcontractor_id)
                              : [...prev, c.subcontractor_id])}
                          />
                          <span className="font-medium text-foreground">{c.full_name}</span>
                          {c.crew_identifier && (
                            <span className="text-muted-foreground">{c.crew_identifier}</span>
                          )}
                          {!c.reachable && (
                            <span className="ml-auto text-[10px] uppercase tracking-widest text-amber-600">no device</span>
                          )}
                        </label>
                      );
                    })}
                  {allCrew.length === 0 && (
                    <p className="p-3 text-xs text-muted-foreground">No crew to choose from.</p>
                  )}
                </div>
                {pickedIds.length > 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    {pickedIds.length} selected. Naming someone offers it to them even if they
                    already have a route that day — the clash is shown below.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="wo-date" className="text-xs">Date</Label>
              <Input id="wo-date" type="date" value={offerDate} onChange={(e) => setOfferDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="wo-time" className="text-xs">Start time</Label>
              <Input id="wo-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="wo-heads" className="text-xs">People needed</Label>
              <Input id="wo-heads" type="number" min="1" value={headcount}
                     onChange={(e) => setHeadcount(e.target.value)} />
            </div>
          </div>

          <div>
            <Label htmlFor="wo-depot" className="text-xs">Depot to confirm to</Label>
            <select
              id="wo-depot"
              value={depotId}
              onChange={(e) => setDepotId(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">No depot confirmation</option>
              {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              When they accept
            </Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                { k: 'none', label: 'Just record the answer' },
                { k: 'availability', label: 'Mark them available' },
                { k: 'allocation', label: 'Allocate them' },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setOutcomeKind(m.k)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    outcomeKind === m.k
                      ? 'border-indigo-500 bg-indigo-600 text-white'
                      : 'border-border bg-background hover:bg-muted'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">
              {outcomeKind === 'none'
                ? 'Nothing is written anywhere. This is how offers have always worked.'
                : outcomeKind === 'availability'
                  ? 'Accepting marks the day available on their calendar. A day they have already set is left alone, and declining never takes a day away.'
                  : 'Accepting puts them in the allocations table for that day, and marks the day available.'}
            </p>

            {outcomeKind === 'allocation' && (
              <div className="mt-3 space-y-3 rounded-xl border border-border bg-background/50 p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="wo-alloc-crew" className="text-xs">Crew</Label>
                    <select
                      id="wo-alloc-crew"
                      value={allocCrewId}
                      onChange={(e) => setAllocCrewId(e.target.value)}
                      className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">Pick a crew…</option>
                      {crews.map((c) => (
                        <option key={c.crew_id} value={c.crew_id}>{c.crew_identifier}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="wo-alloc-role" className="text-xs">Role</Label>
                    <select
                      id="wo-alloc-role"
                      value={allocRole}
                      onChange={(e) => setAllocRole(e.target.value)}
                      className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="lead">Lead</option>
                      <option value="porter">Porter</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="wo-alloc-depot" className="text-xs">Their own depot</Label>
                  <select
                    id="wo-alloc-depot"
                    value={allocDepotId}
                    onChange={(e) => setAllocDepotId(e.target.value)}
                    className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="">Pick a depot…</option>
                    {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>

                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={allocIsOtr} className="mt-0.5"
                         onChange={(e) => setAllocIsOtr(e.target.checked)} />
                  <span>Working at a different depot that day</span>
                </label>

                {allocIsOtr && (
                  <div>
                    <Label htmlFor="wo-alloc-otr" className="text-xs">Depot they are going to</Label>
                    <select
                      id="wo-alloc-otr"
                      value={allocOtrDepotId}
                      onChange={(e) => setAllocOtrDepotId(e.target.value)}
                      className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">Pick a depot…</option>
                      {depots.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                )}

                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={allocIsRetraining} className="mt-0.5"
                         onChange={(e) => setAllocIsRetraining(e.target.checked)} />
                  <span>This is retraining</span>
                </label>

                {allocIncomplete && (
                  <p className="flex items-start gap-1.5 text-[11px] text-amber-600">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    {allocIsOtr && !allocOtrDepotId
                      ? 'Pick the depot they are going to.'
                      : 'An allocation needs a crew, a role and a depot before it can be sent.'}
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="wo-closes" className="text-xs">Closes at (optional)</Label>
            <Input id="wo-closes" type="datetime-local" value={closesAt}
                   onChange={(e) => setClosesAt(e.target.value)} />
            <p className="mt-1 text-[11px] text-muted-foreground">
              After this, accepting is refused. Leave blank to keep it open until it is filled or cancelled.
            </p>
          </div>

          <div>
            <Label htmlFor="wo-notes" className="text-xs">Notes (optional)</Label>
            <Textarea id="wo-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Anything the crew needs to know." />
          </div>

          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)}
                   className="mt-0.5" disabled={!depotId} />
            <span className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              Email the depot automatically when someone accepts
              {!depotId && <span className="italic"> — pick a depot first</span>}
            </span>
          </label>

          {audienceMode !== 'people' && (
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={includeAllocated}
                     onChange={(e) => setIncludeAllocated(e.target.checked)} className="mt-0.5" />
              <span>Include crew who already have a route that day</span>
            </label>
          )}

          <div className="rounded-xl border border-border bg-background/50 p-3">
            <p className="flex items-center gap-2 text-xs font-semibold text-foreground">
              <Users className="h-3.5 w-3.5" />
              {audience === null
                ? 'Working out who is free…'
                : `${audience.length} ${audience.length === 1 ? 'person' : 'people'} would get this`}
            </p>
            {audience !== null && audience.length > 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {reachable} can be pushed now
                {reachable < audience.length && `; ${audience.length - reachable} will only see it in Messages`}
              </p>
            )}
            {audience !== null && audience.length === 0 && (
              <p className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-600">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                {audienceMode === 'people'
                  ? 'Pick at least one person above.'
                  : audienceMode === 'crew'
                    ? (crewId ? 'Nobody on that crew is free that day.' : 'Pick a crew above.')
                    : 'Nobody is free that day. Tick the box above to offer it anyway.'}
              </p>
            )}
            {audience !== null && audience.length > 0 && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                {audience.slice(0, 6).map((a) => a.full_name).join(', ')}
                {audience.length > 6 && ` +${audience.length - 6} more`}
              </p>
            )}
            {audience !== null && audience.some((a) => a.already_allocated) && (
              <p className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-600">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                {audience.filter((a) => a.already_allocated).map((a) => a.full_name).join(', ')}
                {' '}already {audience.filter((a) => a.already_allocated).length === 1 ? 'has' : 'have'} a route that day.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={sending || !kindKey || !(audience?.length) || allocIncomplete}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Send offer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOfferComposer;
